<template>
  <span>
    <v-dialog v-model='updateDialog' persistent max-width='500px'>
      <v-card>
        <v-card-title class='headline font-weight-regular oem-color'>
          File Download
        </v-card-title>
        <v-card-text>
          <span class='oem-text-color'>
            <v-progress-circular
              v-if='showProgress'
              :value='updateValue'
              :size='64'
              :width='7'
              class='mr-2 oem-text-color no-transition'
            >
              <div v-if='updateValue !== null'>
                {{ updateValue }}%
              </div>
            </v-progress-circular>
            {{ updateText }}
          </span>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn v-if='showCloseButton'
            flat class='oem-text-color' @click='close()'>
            Cancel
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-card>
      <v-card-title class='headline font-weight-regular oem-color'>
        System Log
        <v-spacer/>
        <v-btn class='oem-color ma-0' flat @click='reload()'>
          <v-icon>refresh</v-icon>
            Reload system log values
        </v-btn>
        <v-btn flat class='transparent ma-0' @click='$refs.legend.show()' small icon>
          <v-icon class='white--text oem-color'>
            help_outline
          </v-icon>
        </v-btn>
      </v-card-title>
      <v-card-text>
        <div>
          <v-data-table
            :headers='headers'
            :items='storeState.journalEntries'
            must-sort
            pagination-sync.rowPerPage=25
            :rows-per-page-items='paginationItems'
            v-bind:pagination.sync='pagination'
          >
            <template v-slot:items='props'>
              <td width="1%" style="white-space: nowrap;">
                {{ props.item.__REALTIME_TIMESTAMP | realtimeTimestampToDate }}
              </td>
              <td width="1%" style="white-space: nowrap;">
                {{ props.item.__REALTIME_TIMESTAMP | realtimeTimestampToTime }}
              </td>
              <td width="1%" style="white-space: nowrap;">
                {{ props.item._COMM + '[' + props.item._PID +  ']' }}
              </td>

              <td v-bind:class='priorityClass(props.item)'>
                {{ props.item.MESSAGE | formatMessage }}
              </td>
            </template>
          </v-data-table>
          <v-btn
            class='oem-button'
            flat
            @click='downloadJournal()'
          >
            Download Journal
          </v-btn>
          <a ref='downloadLink'></a>
        </div>
      </v-card-text>
    </v-card>
    <helpLegend ref='legend' page='systemlog'></helpLegend>
  </span>
</template>

<script>
import axios from 'axios';
import ajax from '../ajax';
import helpLegend from './BaseLegend.vue';

export default {
  name: 'Log',
  components: {
    helpLegend,
  },
  data() {
    return {
      storeState: this.$store.state,
      headers: [
        { text: 'Date', value: '__REALTIME_TIMESTAMP' },
        { text: 'Time', value: '__REALTIME_TIMESTAMP', sortable: false },
        { text: 'Source', value: '_COMM' },
        { text: 'Event', value: 'MESSAGE' },
      ],
      paginationItems: [10, 20, 50, { text: 'All', value: -1 }],
      pagination: { sortBy: '__REALTIME_TIMESTAMP', descending: true, rowsPerPage: 10 },
      updateDialog: false,
      showProgress: false,
      updateText: '',
      showCloseButton: false,
      updateValue: 0,
      searchItemsSource: null,
    };
  },
  methods: {
    downloadJournal() {
      this.updateText = 'Downloading the file. Please wait.';
      this.updateDialog = true;
      this.showProgress = true;
      this.showCloseButton = true;
      this.updateValue = 0;
      let totValue = 0;

      const CancelToken = axios.CancelToken;
      this.searchItemsSource = CancelToken.source();

      ajax.get('system/journal', {
        cancelToken: this.searchItemsSource.token,
        onDownloadProgress: (progressEvent) => {
          totValue = progressEvent.srcElement.getResponseHeader('Content-Length');
          totValue = Math.round((progressEvent.loaded * 100) / totValue);
          if ((totValue % 2) === 0) {
            this.updateValue = totValue;
          }
          console.log(this.updateValue);
        },
      }).then((response) => {
        setTimeout(() => {
          if (this.showCloseButton) {
            const blob = new Blob([response.data], { type: 'application/octet-stream' });
            const url = window.URL.createObjectURL(blob);
            this.$refs.downloadLink.href = url;
            const now = new Date();
            this.$refs.downloadLink.download = `journal - ${now.toISOString().slice(0, 10)} ${(`0${now.getHours()}`).slice(-2)}${(`0${now.getMinutes()}`).slice(-2)}${(`0${now.getSeconds()}`).slice(-2)}.dat`;
            this.$refs.downloadLink.click();
          }
          this.updateDialog = false;
          this.updateText = '';
          this.showProgress = false;
          this.showCloseButton = false;
        }, 1000);
      }).catch((e) => {
        console.log(e); // eslint-disable-line no-console
        this.updateDialog = false;
        this.updateText = '';
        this.showProgress = false;
        this.showCloseButton = false;
        this.updateValue = 0;
      });
    },
    close() {
      this.updateDialog = false;
      this.updateText = '';
      this.showProgress = false;
      this.showCloseButton = false;
      this.updateValue = 0;
      if (this.searchItemsSource) {
        this.searchItemsSource.cancel('Cancel previous request');
      }
    },
    priorityClass(item) {
      switch (item.PRIORITY) {
        case '0':
        case '1':
        case '2':
        case '3':
          return 'journalError';
        case '4':
          return 'journalWarning';
        case '5':
        case '6':
        case '7':
          return 'journalInfo';
        default:
          return 'journalError';
      }
    },
    reload() {
      this.$store.updateJournal();
    },
  },
  filters: {
    formatMessage(value) {
      let returnValue = value;
      if (typeof value !== 'string') {
        returnValue = '';
        for (let i = 0; i < value.length; i += 1) {
          returnValue += String.fromCharCode(value[i]);
        }
      }
      return returnValue;
    },
    realtimeTimestampToDate(value) {
      return new Date(parseInt(value, 10) / 1000).toLocaleDateString('en-US');
    },
    realtimeTimestampToTime(value) {
      return new Date(parseInt(value, 10) / 1000).toLocaleTimeString('en-US');
    },
  },
};
</script>

<style scoped>
  .journalError {
    color: #ff0000;
    white-space: pre-line;
  }
  .journalWarning {
    color: #000000;
    white-space: pre-line;
  }
  .journalInfo {
    color: #666666;
    white-space: pre-line;
  }
</style>
